<template>
  <div>
    <FavoriteBinanceAlpha
      @apply-filters="handleApplyFilters"
      @reset-filters="handleResetFilters"
    />

    <!-- Coins Table Section -->
    <div v-if="coins.length > 0" class="coins-table-section mx-10">
      <table class="coins-table">
        <thead>
          <tr>
            <th class="text-center">Symbol</th>
            <th class="text-center">Market Cap</th>
            <th class="text-center">FDV</th>
            <th class="text-center">Futures</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="coin in paginatedCoins" :key="coin.symbol" class="coin-row">
            <td class="coin-symbol text-center">{{ coin.symbol }}</td>
            <td class="coin-market-cap text-center">{{ formatNumber(coin.marketCap) }}</td>
            <td class="coin-fdv text-center">{{ formatNumber(coin.fdv) }}</td>
            <td class="coin-status text-center">
              <span v-if="coin.new" class="badge-new">New</span>
              <template v-if="coin.isFutures === 'Y'">
                <span class="badge-futures">Futures</span>
                <span class="badge-spot">Alpha</span>
              </template>
              <template v-else>
                <span class="badge-spot">Alpha</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination-section">
        <div class="pagination-info">
          Showing {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, coins.length) }} of {{ coins.length }}
        </div>
        <div class="pagination-controls">
          <select v-model.number="pageSize" class="page-size-select">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <button @click="previousPage" :disabled="currentPage === 1" class="pagination-btn">← Prev</button>
          <span class="page-info">Page {{ currentPage }} / {{ totalPages }}</span>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">Next →</button>
        </div>
      </div>
    </div>


  </div>
</template>

<script>
import { API_URL } from "../config";
import FavoriteBinanceAlpha from "./favorite/FavoriteBinanceAlpha.vue";

export default {
  name: "BinanceAlpha",
  components: {
    FavoriteBinanceAlpha
  },
  data() {
    return {
      coins: [],
      loading: false,
      error: null,
      currentPage: 1,
      pageSize: 5,
      currentFilters: {
        minMarketCap: null,
        maxMarketCap: null,
        minFdv: null,
        maxFdv: null,
        sortBy: "symbol",
        order: "desc",
        limit: null,
        isFutures: false
      }
    };
  },
  methods: {
    async fetchCoins(useFilters = false) {
      try {
        this.loading = true;
        let url = `${API_URL}/binance-alpha/get-coins`;

        if (useFilters) {
          const params = new URLSearchParams();
          if (this.currentFilters.minMarketCap) params.append('minMarketCap', this.currentFilters.minMarketCap);
          if (this.currentFilters.maxMarketCap) params.append('maxMarketCap', this.currentFilters.maxMarketCap);
          if (this.currentFilters.minFdv) params.append('minFdv', this.currentFilters.minFdv);
          if (this.currentFilters.maxFdv) params.append('maxFdv', this.currentFilters.maxFdv);
          if (this.currentFilters.sortBy) params.append('sortBy', this.currentFilters.sortBy);
          if (this.currentFilters.order) params.append('order', this.currentFilters.order);
          if (this.currentFilters.limit) params.append('limit', this.currentFilters.limit);
          if (this.currentFilters.isFutures) params.append('isFutures', 'Y');

          if (params.toString()) {
            url += '?' + params.toString();
          }
        }

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to fetch coins');
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          this.coins = data;
          this.currentPage = 1;
        } else {
          throw new Error('Invalid data format');
        }
      } catch (error) {
        console.error("Error fetching coins:", error);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },

    handleApplyFilters(filters) {
      this.currentFilters = { ...filters };

      // Tự động set sortBy và order dựa trên filters
      // Nếu có minMarketCap hoặc maxMarketCap → sort theo marketcap từ nhỏ tới lớn
      // Nếu có minFdv hoặc maxFdv → sort theo fdv từ nhỏ tới lớn
      // Nếu có cả 2 → ưu tiên marketcap
      const hasMarketCapFilter = this.currentFilters.minMarketCap || this.currentFilters.maxMarketCap;
      const hasFdvFilter = this.currentFilters.minFdv || this.currentFilters.maxFdv;

      if (hasMarketCapFilter || hasFdvFilter) {
        if (hasMarketCapFilter) {
          this.currentFilters.sortBy = 'marketCap';
        } else if (hasFdvFilter) {
          this.currentFilters.sortBy = 'fdv';
        }
        // Sort từ nhỏ tới lớn (ascending)
        this.currentFilters.order = 'asc';
      }

      this.fetchCoins(true);
    },

    handleResetFilters(filters) {
      this.currentFilters = { ...filters };
      // Reset sortBy và order về mặc định
      this.currentFilters.sortBy = 'symbol';
      this.currentFilters.order = 'desc';
      this.fetchCoins(false);
    },

    formatNumber(value) {
      if (!value) return "0";
      const num = parseFloat(value);
      if (num >= 1e9) {
        return (num / 1e9).toFixed(2) + "B";
      } else if (num >= 1e6) {
        return (num / 1e6).toFixed(2) + "M";
      } else if (num >= 1e3) {
        return (num / 1e3).toFixed(2) + "K";
      }
      return num.toFixed(2);
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.coins.length / this.pageSize);
    },

    paginatedCoins() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.coins.slice(start, end);
    }
  }
};
</script>
